import React, { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  Cell,
  CartesianGrid,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import Card from "../../ui/Card";
import { useTheme } from "../../../contexts/ThemeContext";
import { hexToRgba } from "../../../utils/themes";
import { aggregateAuditSegments, partitionAuditSegments } from "./auditSegmentUtils";

function truncate(str, n) {
  const s = (str || "").trim();
  if (s.length <= n) return s;
  return `${s.slice(0, n - 1)}…`;
}

function AvgFitEndLabel({ x, y, width, height, payload, themeDim }) {
  const avg = payload?.avg_match_score;
  if (avg == null || Number.isNaN(Number(avg))) return null;
  const vx = Number(x) + Number(width) + 6;
  const vy = Number(y) + Number(height) / 2;
  return (
    <text
      x={vx}
      y={vy}
      dominantBaseline="middle"
      fill={themeDim}
      fontSize={11}
      fontFamily="ui-monospace, monospace"
    >
      {`${Math.round(Number(avg))}% avg fit`}
    </text>
  );
}

function MiniScoreBar({ label, value, accent, border }) {
  const hasValue = value != null && !Number.isNaN(Number(value));
  const pct = hasValue ? Math.min(100, Math.max(0, Number(value))) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-3 font-mono text-[10px] text-theme-muted">
        <span>{label}</span>
        <span className="tabular-nums">{hasValue ? `${Math.round(pct)}%` : "n/a"}</span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: hexToRgba(border, 0.55) }}>
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: accent }} />
      </div>
    </div>
  );
}

function RadialScore({ value, accent, border }) {
  const score = Math.min(100, Math.max(0, Number(value) || 0));
  const size = 54;
  const stroke = 6;
  const radius = size / 2 - stroke / 2;
  const circum = 2 * Math.PI * radius;

  return (
    <div className="relative h-[54px] w-[54px] shrink-0">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={hexToRgba(border, 0.6)} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={accent}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${(score / 100) * circum} ${circum}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-mono text-[11px] font-semibold text-theme-on-background tabular-nums">
        {Math.round(score)}%
      </div>
    </div>
  );
}

function TooltipStats({ payload, thresholdHint, theme }) {
  const matchRate = Math.round(Number(payload.match_rate_pct ?? 0));
  return (
    <div className="min-w-[280px] max-w-[340px] space-y-3">
      <div className="flex items-start gap-3">
        <RadialScore value={payload.avg_match_score} accent={theme.accent} border={theme.border} />
        <div className="min-w-0">
          <div className="font-semibold text-theme-on-background text-[14px] leading-tight">{payload.segment}</div>
          <div className="mt-1 font-mono text-[10px] text-theme-muted leading-snug">
            {payload.match_count} matches from {payload.count} cold emails, {thresholdHint}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded border border-theme-border bg-theme-main/60 px-2 py-1.5">
          <div className="font-mono text-[9px] uppercase tracking-[0.08em] text-theme-muted">Emails</div>
          <div className="text-sm font-semibold text-theme-on-background tabular-nums">{payload.count}</div>
        </div>
        <div className="rounded border border-theme-border bg-theme-main/60 px-2 py-1.5">
          <div className="font-mono text-[9px] uppercase tracking-[0.08em] text-theme-muted">Matches</div>
          <div className="text-sm font-semibold text-theme-on-background tabular-nums">{payload.match_count}</div>
        </div>
        <div className="rounded border border-theme-border bg-theme-main/60 px-2 py-1.5">
          <div className="font-mono text-[9px] uppercase tracking-[0.08em] text-theme-muted">Rate</div>
          <div className="text-sm font-semibold text-theme-on-background tabular-nums">{matchRate}%</div>
        </div>
      </div>
      <div className="space-y-2">
        <MiniScoreBar label="Overall fit" value={payload.avg_match_score} accent={theme.accent} border={theme.border} />
        <MiniScoreBar
          label="Company fit"
          value={payload.avg_company_match_score}
          accent={hexToRgba(theme.accent, 0.68)}
          border={theme.border}
        />
        <MiniScoreBar
          label="Role fit"
          value={payload.avg_role_match_score}
          accent={hexToRgba(theme.accent, 0.44)}
          border={theme.border}
        />
      </div>
    </div>
  );
}

/** Horizontal stacked bar per category: matches plus not-matched cold inbound. */
export default function AuditVolumeIcpBarChart({
  rows,
  title,
  description,
  emptyHint = "Nothing to show for this view yet.",
  maxBars = 15,
  icpThreshold = null,
}) {
  const { theme } = useTheme();
  const grid = hexToRgba(theme.border, 0.35);
  const matchMuted = hexToRgba(theme.accent, 0.16);
  const hoverMatch = hexToRgba(theme.accent, 0.78);
  const hoverBelow = hexToRgba(theme.accent, 0.3);
  const [activeSegment, setActiveSegment] = useState(null);

  const { chartData, allGeneric } = useMemo(() => {
    const raw = aggregateAuditSegments(rows);
    const { labeled } = partitionAuditSegments(raw);
    const data = labeled.slice(0, maxBars).map((row) => {
      const match = row.icp_match_count ?? 0;
      const below = Math.max(0, (row.count ?? 0) - match);
      return {
        ...row,
        label: truncate(row.segment, 44),
        match_count: match,
        below_threshold_count: below,
        match_rate_pct: row.icp_match_rate_pct ?? 0,
      };
    });
    return {
      chartData: data,
      allGeneric: labeled.length === 0 && raw.length > 0,
    };
  }, [rows, maxBars]);

  const chartHeight = Math.min(420, 56 + chartData.length * 44);
  const thresholdHint = icpThreshold != null ? `${icpThreshold}% minimum fit score` : "minimum fit score";

  const titleRow = (
    <h3 className="font-display font-bold italic text-lg text-theme-on-background min-w-0 flex-1 mb-1">
      {title}
    </h3>
  );

  if (!rows?.length) {
    return (
      <Card className="p-4 md:p-5">
        {titleRow}
        <p className="text-theme-muted text-sm">{emptyHint}</p>
      </Card>
    );
  }

  if (allGeneric) {
    return (
      <Card className="p-4 md:p-5">
        {titleRow}
        <p className="text-theme-muted text-sm leading-relaxed">
          Every pitch here fell into generic categories (for example Unknown). There are no named categories to chart yet.
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-4 md:p-5">
      {titleRow}
      {description ? (
        <p className="font-mono text-[11px] text-theme-dim mb-2 leading-snug">{description}</p>
      ) : null}
      <div className="w-full" style={{ height: chartHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={chartData}
            margin={{ top: 4, right: 72, left: 8, bottom: 4 }}
            barCategoryGap={8}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={grid} horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={{ fill: theme.dim, fontSize: 11 }} />
            <YAxis
              type="category"
              dataKey="label"
              width={236}
              tick={{ fill: theme.text, fontSize: 12 }}
              interval={0}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const p = payload[0].payload;
                return (
                  <div
                    className="rounded-lg border border-theme-border px-3 py-2 text-sm"
                    style={{ background: theme.panel, color: theme.text }}
                  >
                    <TooltipStats payload={p} thresholdHint={thresholdHint} theme={theme} />
                  </div>
                );
              }}
              cursor={{ fill: hexToRgba(theme.accent, 0.06) }}
            />
            <Legend wrapperStyle={{ fontSize: 10, fontFamily: "monospace", color: theme.text }} />
            <Bar
              dataKey="match_count"
              stackId="a"
              name="Match"
              fill={theme.accent}
              radius={[0, 6, 6, 0]}
              barSize={22}
              onMouseEnter={(entry) => setActiveSegment(entry?.segment || entry?.payload?.segment || null)}
              onMouseLeave={() => setActiveSegment(null)}
            >
              {chartData.map((entry) => (
                <Cell
                  key={`match-${entry.segment}`}
                  fill={activeSegment === entry.segment ? hoverMatch : theme.accent}
                  fillOpacity={activeSegment && activeSegment !== entry.segment ? 0.32 : 1}
                />
              ))}
            </Bar>
            <Bar
              dataKey="below_threshold_count"
              stackId="a"
              name="Not matched"
              fill={matchMuted}
              radius={[0, 6, 6, 0]}
              barSize={22}
              onMouseEnter={(entry) => setActiveSegment(entry?.segment || entry?.payload?.segment || null)}
              onMouseLeave={() => setActiveSegment(null)}
            >
              {chartData.map((entry) => (
                <Cell
                  key={`below-${entry.segment}`}
                  fill={activeSegment === entry.segment ? hoverBelow : matchMuted}
                  fillOpacity={activeSegment && activeSegment !== entry.segment ? 0.32 : 1}
                />
              ))}
              <LabelList content={(props) => <AvgFitEndLabel {...props} themeDim={theme.dim} />} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
